import "bootstrap/dist/css/bootstrap-grid.rtl.min.css";
import { useFormik } from "formik";
import React, { useState } from "react";
import { useModalTicket } from "../../hooks/useModalTicket";
import AvatarIcon from "../../images/avatar-icon.svg";
import BoxIcon from "../../images/box-icon.svg";
import PinAddressIcon from "../../images/pin-address.svg";
import CloseIcon from "../../images/close.svg";

import {
  initialValues,
  Product,
  validationSchemaStep1,
  validationSchemaStep2,
  validationSchemaStep3,
} from "../../models/ticket";
import { api } from "../../services/api";
import { calculateTotalValue } from "../../utils/calculateTotalValue";
import { formatCurrency } from "../../utils/formatCurrency";
import { Modal } from "../Modal";
import { ProgressBar } from "../ProgressBar";
import { Step1 } from "./Step1";
import { Step2 } from "./Step2";
import { Step3 } from "./Step3";
import { Step4 } from "./Step4";
import { Step5 } from "./Step5";
import "./styles.scss";

interface ModalSubmitProps {
  isOpen: boolean;
}

function ModalSubmit({ isOpen }: ModalSubmitProps) {
  const [file, setFile] = useState(undefined);

  const [isSubmited, setIsSubmited] = useState(false);

  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { toggleModal } = useModalTicket();

  const [isValidCaptcha, setIsValidCaptcha] = useState(false);

  const titlesSteps = [
    { title: "Dados Pessoais", icon: AvatarIcon },
    { title: "Endereço", icon: PinAddressIcon },
    { title: "Dados da compra", icon: BoxIcon },
    { title: "Confirmar dados", icon: BoxIcon },
    { title: "Enviar boleto", icon: BoxIcon },
  ];

  const [currentStep, setCurrentStep] = useState(1);

  function getValidationSchema() {
    if (currentStep === 1) return validationSchemaStep1;
    else if (currentStep === 2) return validationSchemaStep2;
    else if (currentStep === 3) return validationSchemaStep3;
  }

  const {
    isValid,
    errors,
    values,
    isValidating,
    isSubmitting,
    setValues,
    handleSubmit,
    handleChange,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: getValidationSchema(),
    onSubmit: async (data) => {
      try {
        if (currentStep !== titlesSteps.length) {
          setCurrentStep(currentStep + 1);
        } else {
          const formData = new FormData();

          if (!file) {
            throw new Error("No file present");
          }
          formData.append("file", file);

          formData.append(
            "data",
            JSON.stringify({
              ...data,
              value: formatCurrency(calculateTotalValue(data.productList)),
            })
          );

          await api.post("/send-mail", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          setIsSubmited(true);
          setAcceptedTerms(false);
        }
      } catch (error) {
        console.log(error);
      }
    },
  });

  function handleClose() {
    setIsSubmited(false);
    setFile(undefined);
    resetForm();
    toggleModal();
    setCurrentStep(1);
  }

  function handleProducts(data: Product) {
    setValues({
      ...values,
      productList: [...values.productList, data],
    });
  }

  function handleDeleteProducts(id: number) {
    setValues({
      ...values,
      productList: values.productList.filter((product) => id !== product.id),
    });
  }

  function handlePrevStep() {
    if (currentStep !== 1) {
      setCurrentStep(currentStep - 1);
    }
  }

  function handleNextStep() {
    if (currentStep !== titlesSteps.length) {
      setCurrentStep(currentStep + 1);
    }
  }

  async function handleChangeCEP(cep: string) {
    const regexCep = /^\d{5}-\d{3}$/;

    if (!cep || !regexCep.test(cep)) return;

    const { data } = await api.get(`/cep/${cep}`);

    setValues({
      ...values,
      neighborhood: data.neighborhood,
      street: data.street,
      state: data.state,
      city: data.city,
    });
  }

  return (
    <Modal onRequestClose={handleClose} isOpen={isOpen}>
      <div className="header-modal mb-3">
        <button type="button" className="close-button" onClick={handleClose}>
          <img src={CloseIcon} alt="CloseIcon" />
        </button>
        <div className="flex">
          <img
            src={titlesSteps[currentStep - 1].icon}
            alt="Icon Current Step"
          />
          <h1 className="title">{titlesSteps[currentStep - 1].title}</h1>
        </div>
        <span className={`step ${isSubmited ? "finished" : ""}`}>
          {currentStep.toString().padStart(2, "0")} /{" "}
          {titlesSteps.length.toString().padStart(2, "0")}
        </span>
      </div>

      <div className="mb-4">
        <ProgressBar
          colorProgressBar="#13EA13"
          colorContainerProgressBar="#F4F5F4"
          progress={currentStep * (100 / titlesSteps.length)}
        />
      </div>

      <form onSubmit={handleSubmit}>
        {currentStep === 1 && (
          <Step1 errors={errors} handleChange={handleChange} values={values} />
        )}

        {currentStep === 2 && (
          <Step2
            errors={errors}
            handleChange={handleChange}
            values={values}
            handleChangeCEP={handleChangeCEP}
          />
        )}

        {currentStep === 3 && (
          <Step3
            errors={errors}
            handleChange={handleChange}
            values={values}
            listProducts={values.productList}
            handleProducts={handleProducts}
            handleDeleteProducts={handleDeleteProducts}
            setValues={setValues}
          />
        )}

        {currentStep === 4 && <Step4 values={values} />}

        {currentStep === 5 && (
          <Step5
            setIsValidCaptcha={setIsValidCaptcha}
            setFile={setFile}
            file={file}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isValidCaptcha={isValidCaptcha}
            isValidating={isValidating}
            isSubmited={isSubmited}
            acceptedTerms={acceptedTerms}
            handlePrevStep={handlePrevStep}
            setAcceptedTerms={setAcceptedTerms}
          />
        )}

        <div className="footer-modal">
          {currentStep !== titlesSteps.length && (
            <div className="container-buttons">
              <button
                className="back-button"
                type="button"
                onClick={handlePrevStep}
                disabled={currentStep === 1}
              >
                Voltar
              </button>
              <button
                className="prev-button"
                type="submit"
                disabled={currentStep === 3 && values.productList.length === 0}
              >
                Próximo
              </button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

export { ModalSubmit };
